/**
 * Handwritten TanStack Query hooks wrapping the generated SDK.
 *
 * Pattern:
 *   - One query/mutation hook per endpoint.
 *   - `queryKey` is the hook's stable identifier. Mutations invalidate the
 *     keys whose data they make stale.
 *   - All hooks return the standard TanStack Query result, so consumers can
 *     destructure `{ data, isLoading, error, mutate, ... }` as usual.
 *
 * To add a hook for a new endpoint, follow the existing examples — copy a
 * useQuery wrapper for a GET, a useMutation wrapper for a POST/PUT/DELETE,
 * and call `queryClient.invalidateQueries` for any keys the mutation
 * affects.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useSyncExternalStore } from "react";

import { type ParticipantAgendaRow } from "@/lib/agenda";
import { client } from "@/lib/api/client";
import {
  adminActivityActions,
  adminActivityAnalytics,
  adminDeleteUser,
  adminGetUser,
  adminInviteUser,
  adminListActivity,
  adminListUsers,
  adminUpdateUser,
  changePassword,
  getActivity,
  getHealth,
  getMe,
  getMeDetail,
  login,
  logout,
  signup,
  updateProfile,
} from "@/lib/api/generated";

// ─── Public queries / mutations ──────────────────────────────────────────

export function useHealth() {
  return useQuery({
    queryKey: ["health"],
    queryFn: async () => {
      const { data } = await getHealth();
      return data;
    },
  });
}

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const { data } = await getMe();
      return data;
    },
    // Don't retry on a 401 — most pages are public now, so no session just
    // means an anonymous participant view rather than an error state.
    retry: false,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { email: string; password: string; rememberMe?: boolean }) => {
      const { data } = await login({
        body: {
          email: vars.email,
          password: vars.password,
          remember_me: vars.rememberMe ?? false,
        },
      });
      return data;
    },
    onSuccess: () => {
      // A login can follow a session that ended WITHOUT the in-app logout
      // (expired/cleared cookie), so the cache may still hold the previous
      // identity's data — and a different user logging in on the same tab
      // would briefly see it. Clear everything, mirroring useLogout; the new
      // session refetches what it needs (including "me").
      queryClient.clear();
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await logout();
    },
    onSuccess: () => {
      // Clear the entire cache so no stale per-user data leaks across sessions.
      queryClient.clear();
    },
  });
}

export function useSignup() {
  return useMutation({
    mutationFn: async (vars: {
      email: string;
      password: string;
      firstName: string;
      lastName: string;
    }) => {
      const { data } = await signup({
        body: {
          email: vars.email,
          password: vars.password,
          first_name: vars.firstName,
          last_name: vars.lastName,
        },
      });
      return data;
    },
  });
}

// ─── Profile ─────────────────────────────────────────────────────────────

export function useMeDetail() {
  return useQuery({
    queryKey: ["me", "detail"],
    queryFn: async () => {
      const { data } = await getMeDetail();
      return data;
    },
    retry: false,
  });
}

export function useActivity() {
  return useQuery({
    queryKey: ["activity"],
    queryFn: async () => {
      const { data } = await getActivity();
      return data ?? [];
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { firstName: string; lastName: string }) => {
      await updateProfile({
        body: { first_name: vars.firstName, last_name: vars.lastName },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (vars: { currentPassword: string; newPassword: string }) => {
      await changePassword({
        body: { current_password: vars.currentPassword, new_password: vars.newPassword },
      });
    },
  });
}

export function useForgotPassword() {
  return useMutation({
    // Always resolves (the backend returns ok regardless of whether the
    // address exists), so the UI can show the same confirmation either way.
    mutationFn: async (vars: { email: string }) => {
      await client.post({ url: "/auth/forgot-password", body: { email: vars.email } });
    },
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: async (vars: { accessToken: string; newPassword: string }) => {
      await client.post({
        url: "/auth/reset-password",
        body: { access_token: vars.accessToken, new_password: vars.newPassword },
      });
    },
  });
}

// ─── Admin ───────────────────────────────────────────────────────────────

export function useAdminUsers() {
  return useQuery({
    queryKey: ["admin", "users"],
    queryFn: async () => {
      const { data } = await adminListUsers();
      return data ?? [];
    },
  });
}

export function useAdminUser(userId: string | null) {
  return useQuery({
    queryKey: ["admin", "users", userId],
    queryFn: async () => {
      if (!userId) {
        return null;
      }
      const { data } = await adminGetUser({ path: { user_id: userId } });
      return data;
    },
    enabled: !!userId,
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      userId: string;
      firstName?: string;
      lastName?: string;
      role?: string;
    }) => {
      await adminUpdateUser({
        path: { user_id: vars.userId },
        body: {
          first_name: vars.firstName,
          last_name: vars.lastName,
          role: vars.role,
        },
      });
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "users", vars.userId] });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { userId: string }) => {
      await adminDeleteUser({ path: { user_id: vars.userId } });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { email: string }) => {
      const { data } = await adminInviteUser({ body: { email: vars.email } });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

// ─── Admin activity ──────────────────────────────────────────────────────

export interface AdminActivityParams {
  search?: string;
  action?: string;
  userId?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

export function useAdminActivity(params: AdminActivityParams = {}) {
  return useQuery({
    queryKey: ["admin", "activity", params],
    queryFn: async () => {
      const { data } = await adminListActivity({
        query: {
          search: params.search,
          action: params.action,
          user_id: params.userId,
          sort_by: params.sortBy ?? "created_at",
          sort_dir: params.sortDir ?? "desc",
          page: params.page ?? 1,
          page_size: params.pageSize ?? 25,
        },
      });
      return data;
    },
    // Keep the previous page on screen while fetching the next so the table
    // doesn't flash empty between paginations / filter changes.
    placeholderData: (prev) => prev,
  });
}

export function useAdminActivityAnalytics(days = 30) {
  return useQuery({
    queryKey: ["admin", "activity", "analytics", days],
    queryFn: async () => {
      const { data } = await adminActivityAnalytics({ query: { days } });
      return data;
    },
  });
}

export function useAdminActivityActions() {
  return useQuery({
    queryKey: ["admin", "activity", "actions"],
    queryFn: async () => {
      const { data } = await adminActivityActions();
      return data ?? [];
    },
  });
}

// ─── Facilitator shared state ────────────────────────────────────────────────
// Agenda, prep checklists, planning fields and Miro links, stored server-side
// (was per-browser localStorage) so every facilitator/admin shares one copy.

const FACILITATOR_STATE_KEY = ["facilitator-state"] as const;

export type FacilitatorState = Record<string, unknown>;

// Debounce network writes per key across all components (last write wins).
const facilitatorWriteTimers = new Map<string, ReturnType<typeof setTimeout>>();

// ── Save-status store (module-level; surfaced via useFacilitatorSaveStatus) ──
export type SaveStatus = "idle" | "saving" | "saved" | "error";

let saveStatus: SaveStatus = "idle";
let inFlightWrites = 0;
let batchHadError = false;
const saveStatusListeners = new Set<() => void>();

function setSaveStatus(next: SaveStatus): void {
  if (saveStatus !== next) {
    saveStatus = next;
    saveStatusListeners.forEach((l) => l());
  }
}

function recomputeSaveStatus(): void {
  if (facilitatorWriteTimers.size > 0 || inFlightWrites > 0) {
    setSaveStatus("saving");
  } else {
    setSaveStatus(batchHadError ? "error" : "saved");
    batchHadError = false;
  }
}

export function useFacilitatorSaveStatus(): SaveStatus {
  return useSyncExternalStore(
    (onChange) => {
      saveStatusListeners.add(onChange);
      return () => saveStatusListeners.delete(onChange);
    },
    () => saveStatus,
    () => saveStatus,
  );
}

export const PARTICIPANT_AGENDA_KEY = ["participant-agenda"] as const;

/**
 * The facilitator agenda for every module, as participants may see it.
 *
 * Participants can't read `/facilitator/state` (facilitator/admin only), so
 * `GET /agenda` serves the same rows with the facilitator-only fields removed.
 * Keyed by module id as a string. Used by the participant module page to keep
 * its agenda in step with the facilitator view; it falls back to the
 * hand-written agenda in `data/modules.ts` when a module has no rows yet.
 */
export function useParticipantAgenda() {
  return useQuery({
    queryKey: PARTICIPANT_AGENDA_KEY,
    queryFn: async () => {
      const { data } = await client.get({ url: "/agenda" });
      return (data ?? {}) as Record<string, ParticipantAgendaRow[]>;
    },
    retry: false,
    staleTime: 60_000,
  });
}

export function useFacilitatorState() {
  return useQuery({
    queryKey: FACILITATOR_STATE_KEY,
    queryFn: async () => {
      const { data } = await client.get({ url: "/facilitator/state" });
      return (data ?? {}) as FacilitatorState;
    },
    retry: false,
    staleTime: 0,
  });
}

export function useFacilitatorStateWriter() {
  const queryClient = useQueryClient();

  const setKey = useCallback(
    (key: string, value: unknown): void => {
      // Optimistic: update the cache immediately so the UI stays responsive.
      queryClient.setQueryData<FacilitatorState>(FACILITATOR_STATE_KEY, (prev) => ({
        ...(prev ?? {}),
        [key]: value,
      }));
      // Debounced persist — one in-flight write per key, last value wins.
      const existing = facilitatorWriteTimers.get(key);
      if (existing) {
        clearTimeout(existing);
      }
      setSaveStatus("saving");
      facilitatorWriteTimers.set(
        key,
        setTimeout(() => {
          facilitatorWriteTimers.delete(key);
          inFlightWrites += 1;
          void client
            .put({ url: `/facilitator/state/${encodeURIComponent(key)}`, body: { value } })
            .catch(() => {
              batchHadError = true;
            })
            .finally(() => {
              inFlightWrites -= 1;
              recomputeSaveStatus();
            });
        }, 500),
      );
    },
    [queryClient],
  );

  const resetAll = useCallback(async (): Promise<void> => {
    setSaveStatus("saving");
    try {
      await client.delete({ url: "/facilitator/state" });
      queryClient.setQueryData<FacilitatorState>(FACILITATOR_STATE_KEY, {});
      setSaveStatus("saved");
    } catch {
      setSaveStatus("error");
    }
  }, [queryClient]);

  return { setKey, resetAll };
}
